/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { IProgramModuleEvidencePart } from "@interfaces/Components";
import EvidenceContentCard from "@components/common/EvidenceContentCard";
import { EvidencePart, IEvidence, IUser, LinkedPartType } from "@interfaces/Interfaces";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import { CreateEvidencePart } from "@components/programs/evidenceParts/ComponentWrapper";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom, userAtom } from "@state/State";
import Collapsible from "react-collapsible";
import { useState } from "react";
import EvidencePartWrapper from "@components/common/EvidencePartWrapper";

const ProgramModuleEvidencePart: React.FC<IProgramModuleEvidencePart> = (props) => {
  const evidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);
  const user = useRecoilValueLoadable<IUser>(userAtom);

  const [collapsibleOpen, setCollapsibleOpen] = useState<boolean>(
    EvidenceUtils.isLearningOutcomesList(props.evidencePart)
  );

  const handleCollapsible = (value: boolean): void => setCollapsibleOpen(value);

  const toggleCollapsible = (): void => setCollapsibleOpen(!collapsibleOpen);

  if (!EvidenceUtils.checkIfEvidencePartIsRequired(props.evidencePart, props.evidenceJSON, props.parentEvidencePart)) {
    return null;
  }

  if (props.evidencePart["Linked Part type"] === LinkedPartType.Child && !props.isChildPart) {
    return null;
  }

  const linkedChildParts = EvidenceUtils.getLinkedChildParts(props.evidencePart);
  const linkedSiblingParts = EvidenceUtils.getLinkedSiblingParts(props.evidencePart);

  let primaryContent = null;

  let id = "";

  if (props.competence) {
    id = `${props.competence?.ID}-${props.evidencePart.ID}-${props.index}`;
  } else if (props.progressCheckCompetence) {
    id = `${props.progressCheckCompetence?.ID}-${props.evidencePart.ID}-${props.index}`;
  } else {
    id = `${props.evidencePart.ID}-${props.index}`;
  }

  if (EvidenceUtils.isBanner(props.evidencePart)) {
    primaryContent = (
      <>
        {CreateEvidencePart(props.evidencePart["Field type"], {
          definition: props.evidencePart,
        })}
      </>
    );
  } else if (
    EvidenceUtils.isNOSSelect(props.evidencePart) ||
    EvidenceUtils.isLearningOutcomesList(props.evidencePart)
  ) {
    primaryContent = (
      <>
        {CreateEvidencePart(props.evidencePart["Field type"], {
          definition: props.evidencePart,
          onValueChanged: props.onValueChanged,
          onInputBlurred: props.onInputBlurred,
          setExistingTagModalVisible: props.setExistingTagModalVisible,
          setEmptyTagModalVisible: props.setEmptyTagModalVisible,
          focusRichText: props.focusRichText,
          blurRichText: props.blurRichText,
          setCollapsibleOpen: handleCollapsible,
          toggleCollapsible: toggleCollapsible,
          collapsibleOpen: collapsibleOpen,
          isLocked: ProgramUtils.isFieldLocked(props.progressCheckCompetence?.ID, props.evidencePart.ID),
          initialValue: ProgramUtils.getEvidencePartInitialValue(
            props.evidencePart,
            props.evidenceJSON,
            evidence.contents,
            props.progressCheckCompetence,
            props.program?.ID
          ),
          id: id,
          program: props.program,
          user: user.contents,
          mandatoryStandards: props.mandatoryStandards,
          optionalStandards: props.optionalStandards,
          learningOutcomes: props.learningOutcomes,
          skills: props.skills ?? [],
          preselectedSkill: props.preselectedSkill,
          preselectedLearningObjective: props.preselectedLearningObjective,
          expandStandardId: props.expandStandardId,
        })}
      </>
    );
  } else if (
    EvidenceUtils.isCompMultiSelect(props.evidencePart) ||
    EvidenceUtils.isNQPCompMultiSelect(props.evidencePart) ||
    EvidenceUtils.isSkillsMultiSelect(props.evidencePart)
  ) {
    if (props.learningOutcomes?.length == 0) {
      primaryContent = null;
    } else {
      primaryContent = (
        <>
          {CreateEvidencePart(props.evidencePart["Field type"], {
            definition: props.evidencePart,
            onValueChanged: props.onValueChanged,
            onInputBlurred: props.onInputBlurred,
            setExistingTagModalVisible: props.setExistingTagModalVisible,
            setEmptyTagModalVisible: props.setEmptyTagModalVisible,
            focusRichText: props.focusRichText,
            blurRichText: props.blurRichText,
            setCollapsibleOpen: handleCollapsible,
            toggleCollapsible: toggleCollapsible,
            collapsibleOpen: collapsibleOpen,
            isLocked: ProgramUtils.isFieldLocked(props.progressCheckCompetence?.ID, props.evidencePart.ID),
            initialValue: ProgramUtils.getEvidencePartInitialValue(
              props.evidencePart,
              props.evidenceJSON,
              evidence.contents,
              props.progressCheckCompetence,
              props.program?.ID
            ),
            id: id,
            program: props.program,
            user: user.contents,
            learningOutcomes: props.learningOutcomes ?? [],
            progressCheckCompetence: props.progressCheckCompetence,
            currentProgressCheckId: props.currentProgressCheckId,
            skills: props.skills ?? [],
            preselectedSkill: props.preselectedSkill,
            preselectedLearningObjective: props.preselectedLearningObjective,
          })}
        </>
      );
    }
  } else {
    const filteredCompetencyOptions = ProgramUtils.getUOCFilteredCompetencyOptions(
      props.evidencePart,
      props.evidenceJSON,
      props.program ?? null,
      user.contents
    );
    const effectiveDefinition = filteredCompetencyOptions
      ? { ...props.evidencePart, "List of values": filteredCompetencyOptions }
      : props.evidencePart;

    primaryContent = (
      <EvidenceContentCard
        mandatory={props.evidencePart.options === "Mandatory" || props.evidencePart["option type"] === "Mandatory"}
        title={props.evidencePart["Display Title"] || props.evidencePart.Name}
        subtitle={props.evidencePart["Display Subtitle"]}
      >
        {CreateEvidencePart(props.evidencePart["Field type"], {
          definition: effectiveDefinition,
          onValueChanged: props.onValueChanged,
          onInputBlurred: props.onInputBlurred,
          setExistingTagModalVisible: props.setExistingTagModalVisible,
          setEmptyTagModalVisible: props.setEmptyTagModalVisible,
          focusRichText: props.focusRichText,
          blurRichText: props.blurRichText,
          setCollapsibleOpen: handleCollapsible,
          toggleCollapsible: toggleCollapsible,
          collapsibleOpen: collapsibleOpen,
          isLocked: ProgramUtils.isFieldLocked(props.progressCheckCompetence?.ID, props.evidencePart.ID),
          initialValue: ProgramUtils.getEvidencePartInitialValue(
            props.evidencePart,
            props.evidenceJSON,
            evidence.contents,
            props.progressCheckCompetence,
            props.program?.ID
          ),
          id: id,
          program: props.program,
          user: user.contents,
          skills: props.skills ?? [],
          preselectedSkill: props.preselectedSkill,
          preselectedLearningObjective: props.preselectedLearningObjective,
          evidenceJSON: props.evidenceJSON,
        })}
        {linkedChildParts && (
          <Collapsible
            trigger={<div />}
            triggerDisabled
            transitionTime={200}
            transitionCloseTime={200}
            open={collapsibleOpen}
          >
            <div className="mt-[12px]">
              {linkedChildParts.map((evidencePart: EvidencePart, index) => (
                <ProgramModuleEvidencePart
                  evidencePart={evidencePart}
                  parentEvidencePart={props.evidencePart}
                  blurRichText={() => props.blurRichText?.()}
                  focusRichText={() => props.focusRichText?.()}
                  evidenceJSON={props.evidenceJSON}
                  index={index}
                  onInputBlurred={() => props.onInputBlurred?.()}
                  onValueChanged={(value: any, isInput?: boolean) =>
                    props.onChildValueChanged && props.onChildValueChanged(evidencePart, value, isInput)
                  }
                  onChildValueChanged={(evidencePart: EvidencePart, value: any, isInput?: boolean) =>
                    props.onChildValueChanged && props.onChildValueChanged(evidencePart, value, isInput)
                  }
                  key={`${props.competence?.Name}-${evidencePart.Name}-${index}`}
                  program={props.program}
                  evidenceDefinitionID={props.evidenceDefinitionID}
                  competence={props.competence}
                  progressCheckCompetence={props.progressCheckCompetence}
                  learningOutcomes={props.learningOutcomes}
                  currentProgressCheckId={props.currentProgressCheckId}
                  setExistingTagModalVisible={() => props.setExistingTagModalVisible?.()}
                  setEmptyTagModalVisible={() => props.setEmptyTagModalVisible?.()}
                  preselectedSkill={props.preselectedSkill}
                  isChildPart
                />
              ))}
            </div>
          </Collapsible>
        )}
      </EvidenceContentCard>
    );
  }

  return (
    <>
      {/* {`${props.evidencePart.Name} - ${props.evidencePart.ID}`} */}
      {primaryContent}
      {linkedSiblingParts.length > 0 &&
        linkedSiblingParts.map((evidencePart: EvidencePart, index) => {
          const triggers = evidencePart["Linked Part trigger"]
            ? evidencePart["Linked Part trigger"].split("\n").map((value) => value.trim())
            : [];

          const selectedValue = props.evidenceJSON[props.evidencePart.Name];

          let included;
          if (typeof selectedValue === "string") {
            included = triggers.includes(selectedValue?.trim());
          } else if (Array.isArray(selectedValue) && typeof selectedValue[0] === "string") {
            included = triggers.some((trigger) => selectedValue.some((value) => value?.trim() === trigger));
          }

          if (!included) {
            return null;
          }

          return (
            <EvidencePartWrapper
              key={`${props.competence?.Name}-${evidencePart.Name}-${index}`}
              isErrorBannerVisible={props.evidencePartWrapperProps?.isErrorBannerVisible ?? false}
              handleElementReference={(ref) =>
                props.evidencePartWrapperProps?.addRefToMandatorySet(ref, evidencePart.Name)
              }
              isMandatory={evidencePart.options === "Mandatory" || evidencePart["option type"] === "Mandatory"}
              isPartIncomplete={props.evidencePartWrapperProps?.mandatoryFields.includes(evidencePart.Name) ?? false}
              wrapperControlsBottomMargin={EvidenceUtils.evidencePartHasNoContainer(evidencePart) ?? false}
            >
              <ProgramModuleEvidencePart
                evidencePart={evidencePart}
                parentEvidencePart={props.evidencePart}
                blurRichText={() => props.blurRichText?.()}
                focusRichText={() => props.focusRichText?.()}
                evidenceJSON={props.evidenceJSON}
                index={index}
                onInputBlurred={() => props.onInputBlurred?.()}
                onValueChanged={(value: any, isInput?: boolean) =>
                  props.onChildValueChanged && props.onChildValueChanged(evidencePart, value, isInput)
                }
                onChildValueChanged={(evidencePart: EvidencePart, value: any, isInput?: boolean) =>
                  props.onChildValueChanged && props.onChildValueChanged(evidencePart, value, isInput)
                }
                program={props.program}
                evidenceDefinitionID={props.evidenceDefinitionID}
                competence={props.competence}
                progressCheckCompetence={props.progressCheckCompetence}
                learningOutcomes={props.learningOutcomes}
                currentProgressCheckId={props.currentProgressCheckId}
                setExistingTagModalVisible={() => props.setExistingTagModalVisible?.()}
                setEmptyTagModalVisible={() => props.setEmptyTagModalVisible?.()}
                skills={props.skills}
                evidencePartWrapperProps={props.evidencePartWrapperProps}
                preselectedSkill={props.preselectedSkill}
                preselectedLearningObjective={props.preselectedLearningObjective}
              />
            </EvidencePartWrapper>
          );
        })}
    </>
  );
};

export default ProgramModuleEvidencePart;
